import React, { useMemo, useCallback } from "react"
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Chip,
  Divider,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import get from "lodash/get"
import { useNavigate } from "react-router-dom"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { CustomIcon, ShopIcon } from "components/icons"
import {
  excludeVatRefundCategory,
  hasVatRefund,
} from "extensions/iconsiam/utils"
import { ComingSoonChip, NewShopChip, VatRefundChip } from "../../chips"

export interface IDirectoryOccupantCardProps {
  data: any
  sx?: SxProps
  endAdornmentSlotClasses?: { [k: string]: any }
  endAdornmentSlots?: { [k: string]: any }
  isNewShop?: boolean
  isComingSoon?: boolean
}

export const HorizontalDirectoryOccupantCard: React.FC<
  IDirectoryOccupantCardProps
> = ({
  data,
  sx,
  endAdornmentSlotClasses,
  endAdornmentSlots,
  isNewShop = false,
  isComingSoon = false,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const categoryIcon = useMemo(
    () => get(data, "properties.local_categories[0].properties.icon_path"),
    [data]
  )
  const { name, logo, level, venueLogo, category, isAd, allLevels } = useMemo(
    () => getFeatureDetail(excludeVatRefundCategory(data)) as IOccupantDetail,
    [data]
  )
  const isVatRefund = useMemo(() => hasVatRefund(data), [data])
  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`HorizontalDirectoryOccupantCard`] || {}
  const {
    root,
    occupantLogo,
    cardContent,
    levelAvatar,
    divider,
    venueLogo: venueLogoStyle,
    titleText,
    detailWrapper,
    infoContainer,
    categoryText,
    chipContainer,
    adsChip,
    newShopChip,
    comingSoonChip,
    vatRefundChip,
    firstSection,
    multiLevelContainer,
    multiLevelAvatar,
  } = styleOverrides

  const hasTagChip = isAd || isNewShop || isComingSoon

  const hasMultipleLevel = allLevels && allLevels.length > 1

  return (
    <Card
      sx={deepmerge(root, sx)}
      onClick={handleClick}
      {...props}
      className={isComingSoon ? "comingSoon" : ""}
    >
      <Box sx={occupantLogo}>
        {hasTagChip && (
          <Box sx={chipContainer}>
            {isAd && (
              <Chip
                label={t("sponsored.search_result")}
                //@ts-ignore
                variant="contained"
                sx={adsChip}
              />
            )}
            {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
            {isComingSoon && !isAd && <ComingSoonChip sx={comingSoonChip} />}
          </Box>
        )}
        <Avatar src={logo} className="occupant-logo-avatar">
          {!logo &&
            (categoryIcon ? <CustomIcon path={categoryIcon} /> : <ShopIcon />)}
        </Avatar>
      </Box>
      <Box sx={cardContent}>
        <Box sx={firstSection}>
          <Typography sx={titleText}>{t(name)}</Typography>
        </Box>
        {isVatRefund && <VatRefundChip sx={vatRefundChip} />}
        <Typography sx={categoryText}>{t(category)}</Typography>
        <Box sx={detailWrapper}>
          <Divider sx={divider} />
          <Box sx={infoContainer}>
            <CardMedia component="img" image={venueLogo} sx={venueLogoStyle} />
            {hasMultipleLevel ? (
              <Box sx={multiLevelContainer}>
                {allLevels.map((levelName, index) => (
                  <Box key={index} sx={multiLevelAvatar}>
                    {t(levelName)}
                  </Box>
                ))}
              </Box>
            ) : (
              <Avatar sx={levelAvatar}>{t(level)}</Avatar>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
